/**
 * CreateEventFromPublicationForm
 * Form to create an Event from a DJEN publication
 * Pre-filled with data extracted from the publication
 */

import { useEffect, useId, useState } from 'react'
import { Calendar, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { DJENPublication } from '@/types/publication'
import { PublicationClassification } from '@/utils/publication-classifier'
import { Client } from '@/types/client'
import { clientService } from '@/services/client-service'
import { eventService } from '@/services/calendar-service'
import { QuickClientModal } from '@/components/QuickClientModal'
import { toast } from 'sonner'
import type { EventType } from '@/types/calendar'

interface CreateEventFromPublicationFormProps {
  publication: DJENPublication
  classification: PublicationClassification
  onSuccess: () => void
  onCancel: () => void
}

export function CreateEventFromPublicationForm({
  publication,
  classification,
  onSuccess,
  onCancel
}: CreateEventFromPublicationFormProps) {
  const fieldId = useId()
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(true)
  const [clientSearchTerm, setClientSearchTerm] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [showQuickClientModal, setShowQuickClientModal] = useState(false)

  const [formData, setFormData] = useState({
    title: `${classification.extractedData.hearingType || 'Audiência'} - Processo ${publication.numeroProcessoComMascara}`,
    description: `Publicação DJEN: ${publication.tipoComunicacao}\n\nTribunal: ${publication.siglaTribunal}\nÓrgão: ${publication.nomeOrgao}\n\nTexto da publicação:\n${publication.texto.substring(0, 500)}${publication.texto.length > 500 ? '...' : ''}`,
    eventType: (classification.suggestedEventType || 'AUDIENCIA') as EventType,
    eventDate: classification.extractedData.date || '',
    startTime: classification.extractedData.startTime || '',
    endTime: classification.extractedData.endTime || '',
    allDay: !classification.extractedData.startTime,
    location: classification.extractedData.location || '',
    locationType: 'PRESENCIAL' as 'PRESENCIAL' | 'VIRTUAL' | 'HIBRIDO',
    onlineMeetingLink: '',
    clientId: '',
    caseId: '',
    courtName: classification.extractedData.courtName || publication.siglaTribunal || '',
    courtRoom: classification.extractedData.location?.includes('Sala') ? classification.extractedData.location : '',
    judgeName: '',
    hearingType: classification.extractedData.hearingType || '',
    priority: 'HIGH' as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT',
    reminderMinutes: [1440, 60, 15], // 1 dia, 1 hora, 15 min
    tags: ['DJEN', publication.tipoComunicacao],
    publicationId: publication.id
  })

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const response = await clientService.getAllClients(
        { status: 'ACTIVE' },
        { page: 1, limit: 100, sort: 'name:asc' }
      )
      setClients(response.clients)
    } catch (error) {
      console.error('Error loading clients:', error)
      toast.error('Erro ao carregar clientes')
    } finally {
      setLoadingClients(false)
    }
  }

  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON' && client.personalData) {
      return client.personalData.fullName || 'Sem nome'
    } else if (client.clientType === 'COMPANY' && client.companyData) {
      return client.companyData.tradingName || client.companyData.companyName || 'Sem nome'
    }
    return 'Sem nome'
  }

  const filteredClients = clients.filter(client => {
    const displayName = getClientDisplayName(client).toLowerCase()
    return displayName.includes(clientSearchTerm.toLowerCase())
  })

  const handleClientSelect = (client: Client) => {
    setClientSearchTerm(getClientDisplayName(client))
    setFormData(prev => ({ ...prev, clientId: client.id }))
    setShowClientDropdown(false)
  }

  const handleClientCreated = async (newClient: Client) => {
    setClientSearchTerm(getClientDisplayName(newClient))
    setFormData(prev => ({ ...prev, clientId: newClient.id }))
    setShowQuickClientModal(false)
    await loadClients()
    toast.success('Cliente criado com sucesso!')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.clientId) {
      toast.error('Selecione um cliente')
      return
    }

    if (!formData.eventDate) {
      toast.error('Informe a data do evento')
      return
    }

    try {
      setLoading(true)

      await eventService.create({
        ...formData,
        originType: 'from_publication',
        originId: publication.id.toString(),
        originMetadata: {
          numeroProcesso: publication.numeroProcesso,
          tribunal: publication.siglaTribunal,
          orgao: publication.nomeOrgao
        }
      })

      onSuccess()
    } catch (error) {
      console.error('Error creating event:', error)
      toast.error('Erro ao criar evento')
    } finally {
      setLoading(false)
    }
  }

  const required = <span className="text-destructive">*</span>

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Dados extraídos */}
        <div className="rounded-lg border bg-muted/40 p-3">
          <h4 className="mb-2 flex items-center gap-2 font-medium">
            <Calendar className="size-4 text-muted-foreground" aria-hidden="true" />
            Dados extraídos da publicação
          </h4>
          <div className="grid grid-cols-1 gap-2 break-words text-sm text-muted-foreground sm:grid-cols-2">
            {formData.eventDate && (
              <div><strong>Data:</strong> {new Date(formData.eventDate).toLocaleDateString('pt-BR')}</div>
            )}
            {formData.startTime && (
              <div><strong>Horário:</strong> {formData.startTime}</div>
            )}
            {formData.location && (
              <div className="sm:col-span-2"><strong>Local:</strong> {formData.location}</div>
            )}
            {formData.hearingType && (
              <div><strong>Tipo:</strong> {formData.hearingType}</div>
            )}
          </div>
        </div>

        <FormField label={<>Título do Evento {required}</>} htmlFor={`${fieldId}-title`}>
          <Input
            id={`${fieldId}-title`}
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
          />
        </FormField>

        <FormField label={<>Tipo de Evento {required}</>} htmlFor={`${fieldId}-type`}>
          <NativeSelect
            id={`${fieldId}-type`}
            value={formData.eventType}
            onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value as EventType }))}
            required
          >
            <option value="AUDIENCIA">Audiência</option>
            <option value="REUNIAO">Reunião</option>
            <option value="COMPROMISSO">Compromisso</option>
            <option value="OUTRO">Outro</option>
          </NativeSelect>
        </FormField>

        <FormField label={<>Cliente {required}</>} htmlFor={`${fieldId}-client`}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Input
                id={`${fieldId}-client`}
                value={clientSearchTerm}
                onChange={(e) => {
                  setClientSearchTerm(e.target.value)
                  setShowClientDropdown(true)
                }}
                onFocus={() => setShowClientDropdown(true)}
                placeholder={loadingClients ? 'Carregando...' : 'Buscar cliente...'}
                disabled={loadingClients}
                autoComplete="off"
              />
              {showClientDropdown && filteredClients.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg">
                  {filteredClients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleClientSelect(client)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      {getClientDisplayName(client)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowQuickClientModal(true)}
              className="shrink-0"
            >
              Novo Cliente
            </Button>
          </div>
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={<>Data {required}</>} htmlFor={`${fieldId}-date`}>
            <Input
              id={`${fieldId}-date`}
              type="date"
              value={formData.eventDate}
              onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
              required
            />
          </FormField>
          <div className="flex items-center gap-2 sm:pt-6">
            <input
              id={`${fieldId}-all-day`}
              type="checkbox"
              checked={formData.allDay}
              onChange={(e) => setFormData(prev => ({ ...prev, allDay: e.target.checked }))}
              className="size-4 rounded-sm border-input accent-primary"
            />
            <Label htmlFor={`${fieldId}-all-day`} className="font-normal">Dia inteiro</Label>
          </div>
        </div>

        {!formData.allDay && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Horário de início" htmlFor={`${fieldId}-start`}>
              <Input
                id={`${fieldId}-start`}
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              />
            </FormField>
            <FormField label="Horário de término" htmlFor={`${fieldId}-end`}>
              <Input
                id={`${fieldId}-end`}
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
              />
            </FormField>
          </div>
        )}

        <FormField label="Local" htmlFor={`${fieldId}-location`}>
          <Input
            id={`${fieldId}-location`}
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="Sala 301, Fórum Central, etc."
          />
        </FormField>

        <FormField label={<>Prioridade {required}</>} htmlFor={`${fieldId}-priority`}>
          <NativeSelect
            id={`${fieldId}-priority`}
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as never }))}
            required
          >
            <option value="LOW">Baixa</option>
            <option value="MEDIUM">Média</option>
            <option value="HIGH">Alta</option>
            <option value="URGENT">Urgente</option>
          </NativeSelect>
        </FormField>

        <FormField label="Observações" htmlFor={`${fieldId}-description`}>
          <Textarea
            id={`${fieldId}-description`}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={4}
          />
        </FormField>

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Criando...
              </>
            ) : (
              <>
                <Calendar className="size-4" aria-hidden="true" />
                Criar Evento
              </>
            )}
          </Button>
        </div>
      </form>

      {showQuickClientModal && (
        <QuickClientModal
          isOpen={showQuickClientModal}
          onClose={() => setShowQuickClientModal(false)}
          onClientCreated={handleClientCreated}
        />
      )}
    </>
  )
}
